import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

export interface FeatureFlag {
  key: string;
  name: string;
  description: string;
  category: string;
  locked: boolean;
  enabled?: boolean;
}

export function useFeatureFlags(clientId: string | null) {
  return useQuery<FeatureFlag[]>({
    queryKey: [`/api/feature-flags`, { clientId }],
    queryFn: async () => {
      if (!clientId) return [];
      const response = await fetch(`/api/feature-flags?clientId=${clientId}`);
      if (!response.ok) throw new Error('Failed to fetch feature flags');
      return response.json();
    },
    enabled: !!clientId,
    staleTime: 30000,
  });
}

export function useGlobalFeatureFlags() {
  return useQuery<FeatureFlag[]>({
    queryKey: [`/api/feature-flags`, { global: true }],
    queryFn: async () => {
      const response = await fetch(`/api/feature-flags?global=true`);
      if (!response.ok) throw new Error('Failed to fetch global feature flags');
      return response.json();
    },
    staleTime: 60000,
  });
}

export function useToggleFeatureFlag(clientId: string) {
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ flagKey, enabled }: { flagKey: string; enabled: boolean }) => {
      const response = await fetch(`/api/clients/${clientId}/flags/${flagKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update feature flag');
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [`/api/feature-flags`] });
      toast({
        title: 'Feature Updated',
        description: `Feature flag has been ${data.enabled ? 'enabled' : 'disabled'}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Update Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useAddFeatureFlag(clientId: string) {
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (flagKey: string) => {
      const response = await fetch(`/api/clients/${clientId}/flags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flagKey }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add feature flag');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/feature-flags`] });
      toast({
        title: 'Feature Added',
        description: 'Feature flag has been added to the client',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Add Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useRemoveFeatureFlag(clientId: string) {
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (flagKey: string) => {
      const response = await fetch(`/api/clients/${clientId}/flags/${flagKey}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove feature flag');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/feature-flags`] });
      toast({
        title: 'Feature Removed',
        description: 'Feature flag has been removed from the client',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Remove Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}